/**
 * Export Configuration Module
 *
 * Centralizes all export-related configuration values that were previously
 * scattered across exporter.js, export-controller.js, and export-performance.js.
 *
 * This makes tuning and debugging easier - all tweakable values in one place.
 */

// Re-export performance thresholds for backward compatibility
export {
  PERFORMANCE_THRESHOLDS,
  BACKPRESSURE_THRESHOLDS,
  ENCODER_CONFIG,
  getBackpressureThresholds,
  getPerformanceThresholds,
  ExportPerformanceMonitor,
  validateThresholds,
} from './export-performance.js';

/**
 * Video encoding configuration
 */
export const VIDEO_CONFIG = {
  // Default bitrate (8 Mbps) - suitable for high quality 1080p
  defaultBitrate: 8_000_000,

  // Keyframe interval in seconds (GOP size)
  keyframeIntervalSec: 2,

  // Codec candidates in order of preference
  // Main profile often has better hardware support than High
  codecCandidates: [
    { name: 'Main 4.0', codec: 'avc1.4D4028' },      // Main profile, level 4.0
    { name: 'Main 4.1', codec: 'avc1.4D4029' },      // Main profile, level 4.1
    { name: 'High 4.0', codec: 'avc1.640028' },      // High profile, level 4.0
    { name: 'High 4.1', codec: 'avc1.640029' },      // High profile, level 4.1
    { name: 'Baseline 3.1', codec: 'avc1.42E01F' }, // Baseline profile, level 3.1
  ],

  // Codec preset overrides (keyed by preset name)
  codecPresets: {
    hevc: { name: 'HEVC Main', codec: 'hvc1.1.6.L93.B0' },
    baseline: { name: 'Baseline 3.0', codec: 'avc1.42E01E' },
    main: null, // Use default order (Main is already first)
    high: { name: 'High 4.0', codec: 'avc1.640028' },
    auto: null, // Use default order
  },
};

/**
 * Audio encoding configuration
 */
export const AUDIO_CONFIG = {
  // Default bitrate (192 kbps) - AAC-LC standard
  defaultBitrate: 192_000,

  // AAC-LC codec identifier
  codec: 'mp4a.40.2',
};

/**
 * Timeout configuration
 * All values in milliseconds
 */
export const TIMEOUT_CONFIG = {
  // Maximum wait for visualizer async initialization (WebGPU)
  visualizerInit: 10000,

  // Wait after closing encoder for hardware resources to release
  // VideoToolbox (macOS) and Media Foundation (Windows) need time to clean up
  hardwareRelease: 500,

  // Wait after export completes before restoring preview visualizer
  // Longer than hardwareRelease to ensure GPU is fully available
  gpuCleanup: 3500,

  // Timeout for CDN script loading (mp4-muxer)
  cdnLoad: 10000,

  // Polling interval when waiting for visualizer ready state
  visualizerPollInterval: 50,

  // Delay before hiding progress bar after FSA export
  progressHideDelay: 2000,
};

/**
 * Chunking configuration for muxer output
 */
export const CHUNKING_CONFIG = {
  // Chunk size for File System Access streaming mode (16 MB)
  // Larger chunks reduce I/O overhead
  fsaChunkSize: 16 * 1024 * 1024,

  // Chunk size for in-memory mode (4 MB)
  // Smaller to balance memory usage vs overhead
  memoryChunkSize: 4 * 1024 * 1024,

  // Maximum pending FSA writes before flushing queue
  fsaWriteQueueMax: 100,
};

/**
 * Analysis configuration
 */
export const ANALYSIS_CONFIG = {
  // Default FFT size for frequency analysis
  defaultFftSize: 2048,

  // Progress reporting phases (percentage of total)
  progressPhases: {
    analysis: { start: 0, end: 0.15 },      // 0-15%
    encoding: { start: 0.20, end: 0.90 },   // 20-90%
    finalizing: { start: 0.90, end: 1.0 },  // 90-100%
  },

  // Frames between progress updates (to reduce overhead)
  progressUpdateInterval: 10,
};

/**
 * Logging and stats configuration
 */
export const STATS_CONFIG = {
  // Interval for logging frame stats (milliseconds)
  logIntervalMs: 1000,

  // Frames between cooperative yields for UI responsiveness
  // Lower = more responsive UI, higher = better throughput
  coopYieldInterval: 60,
};

/**
 * Platform-specific configuration
 */
export const PLATFORM_CONFIG = {
  // Platform detection patterns
  platforms: {
    mac: ['mac', 'darwin'],
    windows: ['win'],
    linux: ['linux'],
    chromeos: ['cros'],
  },

  // Platform-specific cleanup delays
  cleanupDelays: {
    mac: 3500,      // VideoToolbox needs more time
    windows: 1000,  // Media Foundation is faster
    linux: 500,     // VA-API/NVENC typically fast
    chromeos: 1000, // Similar to Windows
    default: 500,
  },
};

/**
 * Get platform-appropriate cleanup delay
 * @returns {number} Delay in milliseconds
 */
export function getPlatformCleanupDelay() {
  const platform = (navigator.platform || '').toLowerCase();
  const userAgent = (navigator.userAgent || '').toLowerCase();

  for (const [key, patterns] of Object.entries(PLATFORM_CONFIG.platforms)) {
    if (patterns.some(p => platform.includes(p) || userAgent.includes(p))) {
      return PLATFORM_CONFIG.cleanupDelays[key];
    }
  }

  return PLATFORM_CONFIG.cleanupDelays.default;
}

/**
 * Build complete codec candidates list based on preset
 * @param {string} preset - Codec preset (auto, main, baseline, high, hevc)
 * @returns {Array} Ordered list of codec candidates
 */
export function getCodecCandidates(preset) {
  const candidates = [...VIDEO_CONFIG.codecCandidates];
  const override = VIDEO_CONFIG.codecPresets[preset];

  if (override) {
    // Insert override at front of list
    candidates.unshift(override);
  }

  return candidates;
}

/**
 * Validate export configuration and return warnings
 * @param {Object} config - Export configuration to validate
 * @returns {{valid: boolean, warnings: string[]}}
 */
export function validateExportConfig(config) {
  const warnings = [];

  // Check for known issues
  if (config.videoBitrate > 50_000_000) {
    warnings.push('Video bitrate is very high (>50 Mbps) - may cause encoding issues');
  }

  if (config.width > 3840 || config.height > 2160) {
    warnings.push('Resolution exceeds 4K - hardware encoding may not be available');
  }

  if (config.fps > 60) {
    warnings.push('FPS > 60 may not have hardware encoder support');
  }

  return {
    valid: warnings.length === 0,
    warnings,
  };
}

/**
 * Default export options (merged with user-provided options)
 */
export const DEFAULT_EXPORT_OPTIONS = {
  width: 1920,
  height: 1080,
  fps: 30,
  videoBitrate: VIDEO_CONFIG.defaultBitrate,
  audioBitrate: AUDIO_CONFIG.defaultBitrate,
  videoCodecPreset: 'auto',
  visualizerType: 'bars',
  visualizerConfig: {},
  lyricsEnabled: true,
  analysisFftSize: ANALYSIS_CONFIG.defaultFftSize,
};
